import { mapValues, sum, values } from 'lodash';
import { keysOf } from '../../../utils';
import { mapImpactScoresDatums } from '../../output/scores';
import { ImpactScoresByIVV } from '../../output/scores/types';
import { Valence } from '../../types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ValueSet } from './types';

/**
 * Apply value set to given impact scores
 */
export function applyValueSetToImpactScoresByIVV(
  impactScoresByIVV: ImpactScoresByIVV,
  valueSet: ValueSet,
  nonNormalized?: boolean,
) {
  const valueSetToUse = nonNormalized ? valueSet : normalizeValueSet(valueSet);

  return mapImpactScoresDatums(impactScoresByIVV, datum => {
    return {
      ...datum,
      value: applyValueSet(valueSetToUse, datum.value, datum.impactCode as ImpactCategoryId, datum.valence),
    };
  });
}

/**
 * Normalize the given value set, such that an
 * average weight for each coeffiecient in the value set equals to 1
 */
export function normalizeValueSet(valueSet: ValueSet): ValueSet {
  const impactCodes = keysOf(valueSet);
  const valueSum = sum(
    impactCodes.map(impactCode => {
      return valueSet[impactCode][Valence.POSITIVE] + valueSet[impactCode][Valence.NEGATIVE];
    }),
  );
  const divider = 19 * 2;
  const averageValue = valueSum / divider;
  return mapValues(valueSet, datum => {
    return {
      [Valence.NEGATIVE]: datum[Valence.NEGATIVE] / averageValue,
      [Valence.POSITIVE]: datum[Valence.POSITIVE] / averageValue,
    };
  });
}

/**
 * The toal number of "value" points that we have is NEUTRAL_VALUE *
 * impactTaxonomy.length (which is currently 10 * 19 = 190). The numbers in
 * valueSet can add up to anything, so calculate the fraction of how much this
 * impact has of the total of the values in the valueSet.
 */
export function pickValueSetCoefficient(valueSet: ValueSet, impactCode: ImpactCategoryId, valence: Valence) {
  if (!valueSet[impactCode]) {
    if (impactCode === ('K5' as ImpactCategoryId)) {
      return 0; // temporary hack to avoid problems caused by removed K5 impact
    }
    throw Error(`Value set missing coefficient for impact code ${impactCode}`);
    return 1;
  }
  if (valueSet[impactCode][valence] == null) {
    throw Error(`Value set missing coefficient for impact code ${impactCode}, valence ${valence}`);
  }
  return valueSet[impactCode][valence];
}

/**
 * Apply value set to score for given impact-valence combination
 */
export function applyValueSet(valueSet: ValueSet, score: number, impactCode: ImpactCategoryId, valence: Valence) {
  return pickValueSetCoefficient(valueSet, impactCode, valence) * score;
}

export function getValueSetWithAllValues(impactCodes: ImpactCategoryId[], value: number) {
  return impactCodes.reduce<ValueSet>((result, code) => {
    result[code] = {
      [Valence.NEGATIVE]: value,
      [Valence.POSITIVE]: value,
    };
    return result;
  }, {} as ValueSet);
}

export function getNeutralValueSet() {
  const impactCodes = values(ImpactCategoryId);
  return getValueSetWithAllValues(impactCodes, 10);
}

export const EQUAL_WEIGHTS_TITLE = 'Equal weights';
